/**
 * Accounting Initialization Service
 * Handles auto-setup of accounting module for new companies
 */

import apiService from './apiService';

export interface InitializationStatus {
  initialized: boolean;
  hasAccounts: boolean;
  hasJournals: boolean;
  hasSettings: boolean;
  accountCount: number;
  journalCount: number;
}

export interface InitializationResult {
  success: boolean;
  accountsCreated: number;
  journalsCreated: number;
  settingsUpdated: boolean;
  message: string;
}

export interface AutoInitResult {
  wasInitialized: boolean;
  alreadySetUp: boolean;
  result?: InitializationResult;
  status: InitializationStatus;
}

class AccountingInitService {
  /**
   * Check if accounting module has been initialized for the company
   */
  async getInitializationStatus(): Promise<InitializationStatus> {
    const response = await apiService.get('/accounting/initialization/status');
    return response.data;
  }

  /**
   * Initialize accounting module with default data
   */
  async initializeAccounting(): Promise<InitializationResult> {
    const response = await apiService.post('/accounting/initialization/initialize', {});
    return response.data;
  }

  /**
   * Auto-initialize if not already initialized (recommended method)
   * This checks status and only initializes if needed
   */
  async autoInitialize(): Promise<AutoInitResult> {
    const response = await apiService.post('/accounting/initialization/auto', {});
    return response.data;
  }

  /**
   * Reset all accounting data (admin only, for testing)
   * WARNING: This will delete all accounting data!
   */
  async resetAccounting(): Promise<{ success: boolean; message: string }> {
    const response = await apiService.delete('/accounting/initialization/reset', {
      data: { confirm: 'DELETE_ALL_ACCOUNTING_DATA' }
    });
    return response;
  }
}

export const accountingInitService = new AccountingInitService();
export default accountingInitService;